/**
 * State-Based Pricing Matrix Component
 * Enterprise pricing management with hierarchical state/county rules.
 * 
 * Features:
 * - Visual hierarchy indicators (Default → State → County)
 * - State dropdown with all 50 US states
 * - County auto-complete from jurisdictions
 * - Effective date management
 * - Overlap prevention with clear error messages
 * - Price resolution preview
 * 
 * Access: Admin and Accounting roles only
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Plus, 
  Edit2, 
  Trash2,
  MapPin,
  Globe,
  Building2,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Search,
  Filter,
  Eye,
  Lock,
  Unlock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Priority states for quick selection
const PRIORITY_STATES = ['DE', 'MD', 'NJ', 'PA', 'NC'];

// Resolution Level Badge
const ResolutionLevelBadge = ({ level }) => {
  const configs = {
    county: { 
      icon: MapPin, 
      label: 'County Override', 
      className: 'bg-purple-500/10 text-purple-600 border-purple-500/20' 
    },
    state: { 
      icon: Globe, 
      label: 'State Override', 
      className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' 
    },
    client_default: { 
      icon: Building2, 
      label: 'Client Default', 
      className: 'bg-slate-500/10 text-slate-600 border-slate-500/20' 
    },
    system_default: { 
      icon: DollarSign, 
      label: 'System Default', 
      className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' 
    }
  };
  
  const config = configs[level] || configs.client_default;
  const Icon = config.icon;
  
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",
      config.className
    )}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Status Badge
const StatusBadge = ({ active }) => (
  <span className={cn(
    "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",
    active 
      ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
      : "bg-slate-500/10 text-slate-500 border-slate-500/20"
  )}>
    <span className={cn("w-1.5 h-1.5 rounded-full", active ? "bg-emerald-500" : "bg-slate-400")} />
    {active ? 'Active' : 'Inactive'}
  </span>
);

/**
 * State-Based Pricing Matrix Component
 */
export const StatePricingMatrix = ({ 
  clientId, 
  clientCode, 
  products = [],
  onRuleChange 
}) => {
  // State
  const [rules, setRules] = useState([]);
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [stateFilter, setStateFilter] = useState('all');
  const { searchInput: searchTerm, setSearchInput: setSearchTerm, debouncedValue: debouncedSearch } = useDebouncedSearch('', 250);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showConfirmOverwrite, setShowConfirmOverwrite] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    product_name: '',
    state: '',
    county: '',
    price: '',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    notes: '',
    is_active: true
  });
  
  // Multi-state bulk creation state
  const [bulkFormData, setBulkFormData] = useState({
    product_id: '',
    product_name: '',
    selectedStates: [],
    price: '',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    notes: ''
  });
  const [bulkPreviewResult, setBulkPreviewResult] = useState(null);
  const [bulkCreating, setBulkCreating] = useState(false);
  
  // Preview state
  const [previewParams, setPreviewParams] = useState({
    product_id: '',
    state: '',
    county: ''
  });
  const [previewResult, setPreviewResult] = useState(null);
  
  // Fetch states on mount
  useEffect(() => {
    fetchStates();
  }, []);
  
  // Fetch rules when clientId changes
  useEffect(() => {
    if (clientId) {
      fetchRules();
    }
  }, [clientId, stateFilter]);
  
  // Fetch counties when state is selected
  useEffect(() => {
    if (formData.state) {
      fetchCounties(formData.state);
    } else {
      setCounties([]);
    }
  }, [formData.state]);
  
  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/pricing-rules/states`);
      setStates(response.data?.states || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };
  
  const fetchCounties = async (state) => {
    try {
      const response = await axios.get(`${API}/pricing-rules/counties/${state}`);
      setCounties(response.data?.counties || []);
    } catch (error) {
      console.error('Error fetching counties:', error);
      setCounties([]);
    }
  };
  
  const fetchRules = async () => {
    if (!clientId) return;
    
    setIsLoading(true);
    try {
      let url = `${API}/pricing-rules/client/${clientId}?include_inactive=true`;
      if (stateFilter !== 'all') {
        url += `&state=${stateFilter}`;
      }
      const response = await axios.get(url);
      setRules(response.data?.rules || []);
    } catch (error) {
      console.error('Error fetching pricing rules:', error);
      toast.error('Failed to load pricing rules');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreateRule = async () => {
    if (!formData.product_id || !formData.price) {
      toast.error('Product and price are required');
      return;
    }
    
    try {
      const payload = {
        client_code_id: clientId,
        product_id: formData.product_id,
        product_name: formData.product_name || products.find(p => p.id === formData.product_id)?.name || formData.product_id,
        state: formData.state || null,
        county: formData.county || null,
        price: parseFloat(formData.price),
        effective_from: formData.effective_from ? new Date(formData.effective_from).toISOString() : null,
        effective_to: formData.effective_to ? new Date(formData.effective_to).toISOString() : null,
        notes: formData.notes || null
      };
      
      await axios.post(`${API}/pricing-rules`, payload);
      toast.success('Pricing rule created successfully');
      setShowAddDialog(false);
      resetForm();
      fetchRules();
      onRuleChange?.();
    } catch (error) {
      console.error('Error creating rule:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create pricing rule';
      toast.error(errorMsg);
    }
  };
  
  const handleUpdateRule = async () => {
    if (!editingRule) return;
    
    try {
      const payload = {
        price: parseFloat(formData.price),
        effective_from: formData.effective_from ? new Date(formData.effective_from).toISOString() : null,
        effective_to: formData.effective_to ? new Date(formData.effective_to).toISOString() : null,
        is_active: formData.is_active,
        notes: formData.notes || null
      };
      
      await axios.patch(`${API}/pricing-rules/${editingRule.id}`, payload);
      toast.success('Pricing rule updated successfully');
      setShowAddDialog(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
      onRuleChange?.();
    } catch (error) {
      console.error('Error updating rule:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to update pricing rule';
      toast.error(errorMsg);
    }
  };
  
  const handleDeactivateRule = async (rule) => {
    if (!window.confirm(`Are you sure you want to deactivate this pricing rule?`)) return;
    
    try {
      await axios.delete(`${API}/pricing-rules/${rule.id}`);
      toast.success('Pricing rule deactivated');
      fetchRules();
      onRuleChange?.();
    } catch (error) {
      console.error('Error deactivating rule:', error);
      toast.error('Failed to deactivate pricing rule');
    }
  };
  
  const handleActivateRule = async (rule) => {
    try {
      await axios.patch(`${API}/pricing-rules/${rule.id}`, {
        is_active: true
      });
      toast.success('Pricing rule activated');
      fetchRules();
      onRuleChange?.();
    } catch (error) {
      console.error('Error activating rule:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to activate pricing rule';
      toast.error(errorMsg);
    }
  };
  
  const handlePreview = async () => {
    if (!previewParams.product_id) {
      toast.error('Please select a product');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/pricing-rules/resolve`, {
        client_code_id: clientId,
        product_id: previewParams.product_id,
        state: previewParams.state || null,
        county: previewParams.county || null
      });
      setPreviewResult(response.data);
    } catch (error) {
      console.error('Error previewing price:', error);
      toast.error('Failed to preview price');
    }
  };
  
  const resetForm = () => {
    setFormData({
      product_id: '',
      product_name: '',
      state: '',
      county: '',
      price: '',
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: '',
      notes: '',
      is_active: true
    });
    setEditingRule(null);
  };
  
  // Multi-state bulk creation functions
  const resetBulkForm = () => {
    setBulkFormData({
      product_id: '',
      product_name: '',
      selectedStates: [],
      price: '',
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: '',
      notes: ''
    });
    setBulkPreviewResult(null);
  };
  
  const handleStateToggle = (stateCode) => {
    setBulkFormData(prev => ({
      ...prev,
      selectedStates: prev.selectedStates.includes(stateCode)
        ? prev.selectedStates.filter(s => s !== stateCode)
        : [...prev.selectedStates, stateCode]
    }));
  };
  
  const handleSelectAllStates = () => {
    setBulkFormData(prev => ({
      ...prev,
      selectedStates: prev.selectedStates.length === states.length 
        ? [] 
        : states.map(s => s.code)
    }));
  };
  
  const handleSelectPriorityStates = () => {
    setBulkFormData(prev => ({
      ...prev,
      selectedStates: PRIORITY_STATES
    }));
  };
  
  const handleBulkPreview = async () => {
    if (!bulkFormData.product_id || bulkFormData.selectedStates.length === 0 || !bulkFormData.price) {
      toast.error('Please select a product, at least one state, and enter a price');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/pricing-rules/multi-state-create/preview`, {
        client_code_id: clientId,
        product_id: bulkFormData.product_id,
        product_name: bulkFormData.product_name || products.find(p => p.id === bulkFormData.product_id)?.name || bulkFormData.product_id,
        states: bulkFormData.selectedStates,
        price: parseFloat(bulkFormData.price)
      });
      setBulkPreviewResult(response.data);
    } catch (error) {
      console.error('Error previewing bulk creation:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview bulk creation');
    }
  };
  
  const handleBulkCreate = async (overwriteExisting = false) => {
    if (!bulkFormData.product_id || bulkFormData.selectedStates.length === 0 || !bulkFormData.price) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setBulkCreating(true);
    
    try {
      const response = await axios.post(`${API}/pricing-rules/multi-state-create`, {
        client_code_id: clientId,
        product_id: bulkFormData.product_id,
        product_name: bulkFormData.product_name || products.find(p => p.id === bulkFormData.product_id)?.name || bulkFormData.product_id,
        states: bulkFormData.selectedStates,
        price: parseFloat(bulkFormData.price),
        effective_from: bulkFormData.effective_from ? new Date(bulkFormData.effective_from).toISOString() : null,
        effective_to: bulkFormData.effective_to ? new Date(bulkFormData.effective_to).toISOString() : null,
        notes: bulkFormData.notes,
        overwrite_existing: overwriteExisting
      });
      
      const { created, overwritten, skipped, errors } = response.data;
      
      if (created > 0 || overwritten > 0) {
        toast.success(`Created ${created} pricing rules${overwritten > 0 ? `, overwrote ${overwritten}` : ''}${skipped > 0 ? `, skipped ${skipped}` : ''}`);
      }
      
      if (errors?.length > 0) {
        toast.error(`${errors.length} rules failed to create`);
      }
      
      setShowBulkDialog(false);
      setShowConfirmOverwrite(false);
      resetBulkForm();
      fetchRules();
      onRuleChange?.();
    } catch (error) {
      console.error('Error creating bulk rules:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pricing rules');
    } finally {
      setBulkCreating(false);
    }
  };
  
  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      product_id: rule.product_id,
      product_name: rule.product_name,
      state: rule.state || '',
      county: rule.county || '',
      price: rule.price?.toString() || '',
      effective_from: rule.effective_from?.split('T')[0] || '',
      effective_to: rule.effective_to?.split('T')[0] || '',
      notes: rule.notes || '',
      is_active: rule.is_active ?? true
    });
    setShowAddDialog(true);
  };
  
  // Get resolution level for display
  const getResolutionLevel = (rule) => {
    if (rule.county) return 'county';
    if (rule.state) return 'state';
    return 'client_default';
  };
  
  // Group rules by product for matrix display
  const rulesByProduct = useMemo(() => {
    const grouped = {};
    rules.forEach(rule => {
      const productId = rule.product_id;
      if (!grouped[productId]) {
        grouped[productId] = {
          product_id: productId,
          product_name: rule.product_name,
          rules: []
        };
      }
      grouped[productId].rules.push(rule);
    });
    return Object.values(grouped);
  }, [rules]);
  
  // Filter rules based on search
  const filteredRules = useMemo(() => {
    if (!debouncedSearch) return rules;
    const term = debouncedSearch.toLowerCase();
    return rules.filter(r => 
      r.product_name?.toLowerCase().includes(term) ||
      r.state?.toLowerCase().includes(term) ||
      r.county?.toLowerCase().includes(term)
    );
  }, [rules, debouncedSearch]);
  
  if (!clientId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Building2 className="w-12 h-12 text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700">Select a Client</h3>
        <p className="text-sm text-slate-500">Choose a client to manage their pricing rules</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* State Filter */}
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by state" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              <SelectItem value="default">Default Only</SelectItem>
              {PRIORITY_STATES.map(code => (
                <SelectItem key={code} value={code}>{code}</SelectItem>
              ))}
              <div className="border-t my-1" />
              {states.filter(s => !PRIORITY_STATES.includes(s.code)).map(s => (
                <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search rules..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-[200px]"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowPreviewDialog(true)}
            className="gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview Price
          </Button>
          <Button 
            variant="outline"
            onClick={() => { resetBulkForm(); setShowBulkDialog(true); }}
            className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Globe className="w-4 h-4" />
            Bulk Add States
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Pricing Rule
          </Button>
        </div>
      </div>
      
      {/* Pricing Rules Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Pricing Rules Matrix</CardTitle>
          <CardDescription>
            Manage state and county-specific pricing for {clientCode}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <DollarSign className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No Pricing Rules</h3>
              <p className="text-sm text-slate-500 mb-4">Add your first pricing rule to get started</p>
              <Button 
                onClick={() => { resetForm(); setShowAddDialog(true); }}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Pricing Rule
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="tf-thead">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">State</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">County</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Price</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Effective From</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Effective To</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Level</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-800">{rule.product_name}</span>
                      </td>
                      <td className="py-3 px-4">
                        {rule.state ? (
                          <span className="text-slate-700">{rule.state}</span>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {rule.county ? (
                          <span className="text-slate-700">{rule.county}</span>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-semibold text-slate-800">
                          ${parseFloat(rule.price).toFixed(2)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">
                          {rule.effective_from?.split('T')[0] || '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">
                          {rule.effective_to?.split('T')[0] || 'Open'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <ResolutionLevelBadge level={getResolutionLevel(rule)} />
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge active={rule.is_active} />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openEditDialog(rule)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit Rule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          {rule.is_active ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleDeactivateRule(rule)}
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Deactivate Rule</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleActivateRule(rule)}
                                    className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Activate Rule</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) { resetForm(); } setShowAddDialog(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Pricing Rule' : 'Add Pricing Rule'}</DialogTitle>
            <DialogDescription>
              {editingRule 
                ? 'Update the pricing rule details below.'
                : 'Create a new pricing rule. Leave State/County empty for client default pricing.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Product Selection */}
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select 
                value={formData.product_id} 
                onValueChange={(value) => {
                  const product = products.find(p => p.id === value);
                  setFormData(prev => ({ 
                    ...prev, 
                    product_id: value,
                    product_name: product?.name || value
                  }));
                }}
                disabled={!!editingRule}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* State Selection */}
            <div className="space-y-2">
              <Label>State (optional)</Label>
              <Select 
                value={formData.state} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, state: value === 'none' ? '' : value, county: '' }))}
                disabled={!!editingRule}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state (or leave empty for default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No State (Client Default)</SelectItem>
                  <div className="px-2 py-1 text-xs text-slate-500 font-medium">Priority States</div>
                  {PRIORITY_STATES.map(code => {
                    const state = states.find(s => s.code === code);
                    return state && (
                      <SelectItem key={code} value={code}>
                        {code} - {state.name}
                      </SelectItem>
                    );
                  })}
                  <div className="border-t my-1" />
                  <div className="px-2 py-1 text-xs text-slate-500 font-medium">All States</div>
                  {states.filter(s => !PRIORITY_STATES.includes(s.code)).map(s => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.code} - {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* County Selection (only if state is selected) */}
            {formData.state && (
              <div className="space-y-2">
                <Label>County (optional)</Label>
                <Select 
                  value={formData.county} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, county: value === 'none' ? '' : value }))}
                  disabled={!!editingRule}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select county (or leave empty for state-level)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No County (State-Level)</SelectItem>
                    {counties.map(county => (
                      <SelectItem key={county} value={county}>{county}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {counties.length === 0 && (
                  <p className="text-xs text-slate-500">No counties found in jurisdictions. You can type a custom county name.</p>
                )}
              </div>
            )}
            
            {/* Price */}
            <div className="space-y-2">
              <Label>Unit Price ($) *</Label>
              <Input 
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
              />
            </div>
            
            {/* Effective Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective From</Label>
                <Input 
                  type="date"
                  value={formData.effective_from}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Effective To (optional)</Label>
                <Input 
                  type="date"
                  value={formData.effective_to}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_to: e.target.value }))}
                  placeholder="Open-ended"
                />
              </div>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input 
                placeholder="Optional notes about this pricing rule"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            
            {/* Active/Inactive Toggle - Only show when editing */}
            {editingRule && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Rule Status</Label>
                  <p className="text-xs text-slate-500">
                    {formData.is_active 
                      ? 'This rule is active and will be used for pricing resolution'
                      : 'This rule is inactive and will not be used'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_active}
                  onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={cn(
                    "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                    formData.is_active ? "bg-emerald-500" : "bg-slate-300"
                  )}
                >
                  <span
                    className={cn(
                      "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-transform",
                      formData.is_active ? "translate-x-5" : "translate-x-0"
                    )}
                  />
                </button>
              </div>
            )}
            
            {/* Level Preview */}
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-2">This rule will be applied at:</p>
              <ResolutionLevelBadge 
                level={formData.county ? 'county' : formData.state ? 'state' : 'client_default'} 
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowAddDialog(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={editingRule ? handleUpdateRule : handleCreateRule}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Price Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Preview Price Resolution</DialogTitle>
            <DialogDescription>
              See how the pricing hierarchy resolves for specific parameters.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select 
                value={previewParams.product_id} 
                onValueChange={(value) => setPreviewParams(prev => ({ ...prev, product_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>State (optional)</Label>
              <Select 
                value={previewParams.state} 
                onValueChange={(value) => setPreviewParams(prev => ({ ...prev, state: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No State</SelectItem>
                  {states.map(s => (
                    <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>County (optional)</Label>
              <Input 
                placeholder="Enter county name"
                value={previewParams.county}
                onChange={(e) => setPreviewParams(prev => ({ ...prev, county: e.target.value }))}
              />
            </div>
            
            <Button onClick={handlePreview} className="w-full gap-2">
              <Eye className="w-4 h-4" />
              Preview Price
            </Button>
            
            {previewResult && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Resolved Price:</span>
                  <span className="text-2xl font-bold text-slate-800">
                    ${parseFloat(previewResult.price).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Resolution Level:</span>
                  <ResolutionLevelBadge level={previewResult.resolution_level} />
                </div>
                {previewResult.matched_rule_id && (
                  <div className="text-xs text-slate-500">
                    Matched Rule: {previewResult.matched_rule_id.slice(0, 8)}...
                  </div>
                )}
                <div className="text-xs text-slate-500 border-t pt-2 mt-2">
                  {previewResult.hierarchy_explanation}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Add States Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={(open) => { if (!open) { resetBulkForm(); } setShowBulkDialog(open); }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-emerald-600" />
              Bulk Add State Pricing
            </DialogTitle>
            <DialogDescription>
              Create pricing rules for multiple states at once. Select states, set a price, and create all rules in one action.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Product Selection */}
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select 
                value={bulkFormData.product_id} 
                onValueChange={(value) => {
                  const product = products.find(p => p.id === value);
                  setBulkFormData(prev => ({ 
                    ...prev, 
                    product_id: value,
                    product_name: product?.name || value
                  }));
                  setBulkPreviewResult(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Price */}
            <div className="space-y-2">
              <Label>Unit Price ($) *</Label>
              <Input 
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={bulkFormData.price}
                onChange={(e) => {
                  setBulkFormData(prev => ({ ...prev, price: e.target.value }));
                  setBulkPreviewResult(null);
                }}
              />
            </div>
            
            {/* State Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Select States *</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllStates}
                    className="text-xs h-7"
                  >
                    {bulkFormData.selectedStates.length === states.length ? 'Deselect All' : 'Select All 50'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectPriorityStates}
                    className="text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    Priority States (DE, MD, NJ, PA, NC)
                  </Button>
                </div>
              </div>
              
              <div className="text-sm text-slate-500">
                {bulkFormData.selectedStates.length} states selected
              </div>
              
              {/* State Grid */}
              <div className="border rounded-lg p-4 max-h-[280px] overflow-y-auto">
                <div className="grid grid-cols-4 gap-2">
                  {states.map(state => (
                    <label 
                      key={state.code}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors text-sm",
                        bulkFormData.selectedStates.includes(state.code) 
                          ? "bg-emerald-50 border border-emerald-200" 
                          : "hover:bg-slate-50 border border-transparent",
                        PRIORITY_STATES.includes(state.code) && "font-medium"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={bulkFormData.selectedStates.includes(state.code)}
                        onChange={() => handleStateToggle(state.code)}
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className={cn(
                        PRIORITY_STATES.includes(state.code) && "text-emerald-700"
                      )}>
                        {state.code}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Effective Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective From</Label>
                <Input 
                  type="date"
                  value={bulkFormData.effective_from}
                  onChange={(e) => setBulkFormData(prev => ({ ...prev, effective_from: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Effective To (optional)</Label>
                <Input 
                  type="date"
                  value={bulkFormData.effective_to}
                  onChange={(e) => setBulkFormData(prev => ({ ...prev, effective_to: e.target.value }))}
                  placeholder="Open-ended"
                />
              </div>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input 
                placeholder="Optional notes for all created rules"
                value={bulkFormData.notes}
                onChange={(e) => setBulkFormData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            
            {/* Preview Button - More prominent styling */}
            <Button 
              type="button"
              variant={bulkPreviewResult ? "outline" : "default"}
              onClick={handleBulkPreview}
              disabled={!bulkFormData.product_id || bulkFormData.selectedStates.length === 0 || !bulkFormData.price}
              className={cn(
                "w-full gap-2",
                !bulkPreviewResult && bulkFormData.selectedStates.length > 0 && bulkFormData.product_id && bulkFormData.price
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : ""
              )}
            >
              <Eye className="w-4 h-4" />
              {bulkPreviewResult 
                ? "Re-run Preview" 
                : `Preview ${bulkFormData.selectedStates.length} State${bulkFormData.selectedStates.length !== 1 ? 's' : ''}`}
            </Button>
            
            {/* Preview Results */}
            {bulkPreviewResult && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">Preview Summary</span>
                  <Badge variant="outline">{bulkPreviewResult.total_states} states</Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <div className="text-emerald-700 font-medium">{bulkPreviewResult.new_count} New Rules</div>
                    <div className="text-emerald-600 text-xs mt-1">Will be created</div>
                  </div>
                  
                  {bulkPreviewResult.existing_count > 0 && (
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                      <div className="text-amber-700 font-medium">{bulkPreviewResult.existing_count} Existing Rules</div>
                      <div className="text-amber-600 text-xs mt-1">Already exist - will be skipped or overwritten</div>
                    </div>
                  )}
                </div>
                
                {bulkPreviewResult.existing_rules?.length > 0 && (
                  <div className="border-t pt-3">
                    <div className="text-sm font-medium text-amber-700 mb-2">Existing Rules (will be skipped unless you choose to overwrite):</div>
                    <div className="max-h-[120px] overflow-y-auto space-y-1">
                      {bulkPreviewResult.existing_rules.map(rule => (
                        <div key={rule.state} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                          <span>{rule.state} - {rule.state_name}</span>
                          <span className="text-slate-500">${rule.existing_price?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {bulkPreviewResult.new_states?.length > 0 && (
                  <div className="border-t pt-3">
                    <div className="text-sm font-medium text-emerald-700 mb-2">New Rules to Create:</div>
                    <div className="flex flex-wrap gap-1">
                      {bulkPreviewResult.new_states.map(state => (
                        <Badge key={state.state} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          {state.state}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {/* Show helper message when preview hasn't been run yet */}
            {!bulkPreviewResult && bulkFormData.selectedStates.length > 0 && bulkFormData.product_id && bulkFormData.price && (
              <div className="w-full text-center text-sm text-slate-500 pb-2 sm:pb-0 sm:text-left sm:flex-1">
                Click "Preview Changes" to check for conflicts before creating rules
              </div>
            )}
            
            {/* Show message when all selected states already have rules */}
            {bulkPreviewResult && bulkPreviewResult.new_count === 0 && bulkPreviewResult.existing_count > 0 && (
              <div className="w-full text-center text-sm text-amber-600 pb-2 sm:pb-0 sm:text-left sm:flex-1">
                All {bulkPreviewResult.existing_count} selected states already have pricing rules
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { resetBulkForm(); setShowBulkDialog(false); }}>
                Cancel
              </Button>
              
              {bulkPreviewResult?.existing_count > 0 && (
                <Button 
                  variant="outline"
                  onClick={() => setShowConfirmOverwrite(true)}
                  disabled={bulkCreating}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  Create & Overwrite Existing
                </Button>
              )}
              
              <Button 
                onClick={() => handleBulkCreate(false)}
                disabled={bulkCreating || !bulkPreviewResult || bulkPreviewResult.new_count === 0}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              >
                {bulkCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creating...
                  </>
                ) : !bulkPreviewResult ? (
                  <>
                    <Eye className="w-4 h-4" />
                    Preview First
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create {bulkPreviewResult.new_count} Rules
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirm Overwrite Dialog */}
      <Dialog open={showConfirmOverwrite} onOpenChange={setShowConfirmOverwrite}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Confirm Overwrite
            </DialogTitle>
            <DialogDescription>
              This will deactivate {bulkPreviewResult?.existing_count || 0} existing pricing rules and create new ones with the new price.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 rounded-lg p-4 text-sm">
              <p className="font-medium text-amber-800 mb-2">The following rules will be replaced:</p>
              <ul className="space-y-1 text-amber-700">
                {bulkPreviewResult?.existing_rules?.map(rule => (
                  <li key={rule.state} className="flex justify-between">
                    <span>{rule.state_name}</span>
                    <span>${rule.existing_price?.toFixed(2)} → ${parseFloat(bulkFormData.price).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmOverwrite(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleBulkCreate(true)}
              disabled={bulkCreating}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {bulkCreating ? 'Processing...' : 'Confirm Overwrite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatePricingMatrix;
